import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook';
import React, { ReactElement, useRef, useState, useEffect, memo } from 'react';
import { ThemeProvider } from 'styled-components';

import ContentArea from 'components/ContentArea';
import Icon from 'components/Icon/Icon';
import { useEventListener, useTabTrap } from 'hooks';
import useAppState from 'hooks/useAppState';
import { A11y } from 'style/components/A11Y';
import getComponentTypeForContent from 'utils/getComponentTypeForContent';

import { Container, Wrapper, Flyout, Dialog, ToggleButton, Content } from './ContactFlyout.styled';
import ContactFlyoutContext from './ContactFlyoutContext';
import ContactFlyoutProps from './ContactFlyoutProps';

const ContactFlyout = ({ label, icon, blocks, identifier, contentId }: ContactFlyoutProps): ReactElement => {
    const sendDataToGTM = useGTMDispatch();
    const dialogRef = useRef<HTMLDivElement>(null);
    const flyoutRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [open, setOpen] = useState(false);
    const [isStepActive, setIsStepActive] = useState(false);
    const [stepHeight, setStepHeight] = useState<number | undefined>(undefined);
    const [gtmTrigger, setGtmTrigger] = useState<string | null>(null);
    const iconSize = '56';
    const hasBlocks = blocks && blocks?.length > 0;
    const { fetchSuccess: isFetchedSuccess } = useAppState();

    // Close flyout when the page fetch completed
    useEffect(() => {
        if (isFetchedSuccess) {
            setOpen(false);
        }
    }, [isFetchedSuccess]);

    useEffect(() => {
        if (gtmTrigger) {
            sendDataToGTM({
                event: 'contact_flyout',
                button_label: label,
                flyout_open: open,
                flyout_trigger: gtmTrigger,
            });
            setGtmTrigger(null);
        }
    }, [open, label, gtmTrigger, sendDataToGTM]);

    useTabTrap(flyoutRef, open && !isStepActive);

    const handleButtonClick = (event: React.MouseEvent | React.KeyboardEvent) => {
        event.stopPropagation();
        setOpen((prev) => !prev);
        setGtmTrigger('button');
    };

    const handleClickOutside = (event: MouseEvent) => {
        const path = event.composedPath();

        if (flyoutRef.current && !path.includes(flyoutRef.current) && open) {
            setOpen(false);
            setGtmTrigger('click-outside');
        }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && open) {
            // Set focus back to the button
            // This is important for accessibility
            // and to ensure the user can interact with the flyout again
            // after closing it with the escape key
            if (buttonRef.current) {
                buttonRef.current.focus();
            }

            setOpen(false);
            setGtmTrigger('escape');
        }
    };

    useEventListener('click', handleClickOutside);
    useEventListener('keydown', handleEscapeKey);

    if (!hasBlocks) {
        return <></>;
    }

    return (
        <ContactFlyoutContext.Provider
            value={{
                isStepActive,
                setIsStepActive,
                open,
                stepHeight,
                setStepHeight,
            }}
        >
            <ThemeProvider
                theme={{
                    open: open,
                }}
            >
                <Wrapper id={identifier} aria-labelledby={`contact-flyout-button-${contentId}`}>
                    <Container>
                        <Flyout ref={flyoutRef}>
                            <ToggleButton
                                id={`contact-flyout-button-${contentId}`}
                                onClick={handleButtonClick}
                                aria-expanded={open}
                                aria-controls={`contact-flyout-dialog-${contentId}`}
                                ref={buttonRef}
                            >
                                <Icon icon={open ? 'close56' : `${icon}${iconSize}`} />
                                <A11y>{label}</A11y>
                            </ToggleButton>
                            <Dialog
                                id={`contact-flyout-dialog-${contentId}`}
                                aria-hidden={!open}
                                role="dialog"
                                aria-label={label}
                                ref={dialogRef}
                            >
                                <Content style={stepHeight ? { height: `${stepHeight}px` } : undefined}>
                                    <ContentArea items={blocks} componentSelector={getComponentTypeForContent} />
                                </Content>
                            </Dialog>
                        </Flyout>
                    </Container>
                </Wrapper>
            </ThemeProvider>
        </ContactFlyoutContext.Provider>
    );
};

export default memo(ContactFlyout);
